import { useState, useMemo } from 'react';
import { useGetMyInquiries } from '@/services/inquiry/queries';
import { useAuthStore } from '@/store/auth';
import InquiryFilters, { type InquiryFilters as InquiryFiltersType } from './inquiry-filters';
import InquiryCard from './inquiry-card';
import { Button } from '@/components/ui/button';
import { Icons } from '@/components/icons';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Inquiry } from '@/services/types';

const ITEMS_PER_PAGE = 5;

export default function MyInquiries() {
  const { user } = useAuthStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<InquiryFiltersType>({});

  // Prepare API filters
  const apiFilters = useMemo(() => ({
    page: currentPage,
    limit: ITEMS_PER_PAGE,
    ...filters
  }), [currentPage, filters]);

  const { data, isLoading, refetch } = useGetMyInquiries(
    user?.id || '',
    apiFilters,
    {
      enabled: !!user?.id,
      staleTime: 1000 * 60 * 2, // 2 minutes
      refetchOnWindowFocus: false
    }
  );

  const inquiries = data?.data || [];
  const totalCount = data?.pagination?.totalDocs || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const handleFiltersChange = (newFilters: InquiryFiltersType) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleInquiryDelete = () => {
    refetch();
  };

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <div className='h-20 bg-gray-100 rounded animate-pulse' />
        <div className='space-y-3'>
          {[...Array(3)].map((_, i) => (
            <div key={i} className='h-32 bg-gray-100 rounded animate-pulse' />
          ))}
        </div>
      </div>
    );
  }

  if (!inquiries.length) {
    return (
      <div className='space-y-4'>
        <InquiryFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          totalCount={totalCount}
        />
        
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <Icons.emptyState />
          <h3 className='mt-4 text-lg font-semibold text-gray-900'>
            No inquiries found
          </h3>
          <p className='mt-2 text-gray-600'>
            {Object.keys(filters).length > 0
              ? 'Try adjusting your filters to see more results.'
              : 'You haven\'t submitted any inquiries yet.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <InquiryFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        totalCount={totalCount}
      />

      <div className='space-y-4'>
        {inquiries.map((inquiry: Inquiry) => (
          <InquiryCard
            key={inquiry.id}
            inquiry={inquiry}
            onDelete={handleInquiryDelete}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='flex items-center justify-between pt-4'>
          <div className='text-sm text-gray-700'>
            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to{' '}
            {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount} inquiries
          </div>
          
          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className='h-4 w-4' />
              Previous
            </Button>
            
            <div className='flex items-center gap-1'>
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                const isCurrentPage = page === currentPage;
                
                return (
                  <Button
                    key={page}
                    variant={isCurrentPage ? 'default' : 'outline'}
                    size='sm'
                    onClick={() => handlePageChange(page)}
                    className='w-8 h-8 p-0'
                  >
                    {page}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant='outline'
              size='sm'
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight className='h-4 w-4' />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}